/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import {
  ChangeDetectorRef,
  Component, OnDestroy,
  OnInit
} from '@angular/core';

import { User       } from '@/models/user';

import { Logger                } from '@/util/logger';

import {UserService} from "@/services/user.service";
import {AuthenticationService} from "@/services/authentication.service";
import {IProjectRow} from "@/screens/administration/i.project.row";
import {ProjectService} from "@/services/project.service";
import {ActivatedRoute, ParamMap} from "@angular/router";
import {Subscription} from "rxjs";
import {first} from "rxjs/operators";
import {IUserRow} from "@/screens/administration/i.user.row";
import {ISubdivisionRow} from "@/screens/administration/i.subdivision.row";
import {Inplace} from "primeng/inplace";
import {IOrganizationRow} from "@/screens/administration/i.organization.row";
import {fromMySQLDate} from "@/util/util";

@Component({
  standalone: false,
  templateUrl: 'project.details.component.html'
})

/**
 *
 * @class ProjectDetailsComponent
 *
 * @brief view/edit details for a specific project
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2022
 *
 */
export class ProjectDetailsComponent implements OnInit, OnDestroy {

  /** the logged-in user */
  public viewer: User               = null;

  /** the project key */
  public key: string                = '';

  /** the project being viewed */
  public project: IProjectRow       = null;

  /** all possible subdivisions */
  public allSubs: ISubdivisionRow[] = [];

  /** our subscription to the current route */
  private routeListen: Subscription = null;

  /** the selected auto-completed user */
  public newUser: IUserRow          = null;

  /** the auto-completed users */
  public searchUsers: IUserRow[]    = [];

  /** the project admins */
  public admins: IUserRow[]         = [];

  /** the project members */
  public members: IUserRow[]        = [];

  /** most recent search term */
  private lastTerm: string          = '';

  /** standard constructor */
  constructor(
    private auth:       AuthenticationService,
    private users:      UserService,
    private route:      ActivatedRoute,
    private prjservice: ProjectService,
    private cdRef:      ChangeDetectorRef
  ) {

    this.project = {
      key:         'NONE',
      name:        'No Project',
      logo_url:    '/assets/images/default-project-logo.png',
      description: 'No Description',
      organization: {
        key:  'None',
        name: 'No Organization'
      },
      subdivisions:  [],
      pending_users: []
    }
  }

  /** check if viewer is an admin for this project */
  isAdmin() {

    let pd: ProjectDetailsComponent = this;
    let viewer: User = pd.auth.userOfSession;

    /*
     * you are a project administrator if:
     *
     *   - you are system admin
     *   - you are an admin in the org of the project
     *
     */

    if (pd.auth.isAdmin()) {

      /* system admin */

      return true;
    }

    let found: boolean = false;

    if (pd.project.admins) {

      pd.project.admins.forEach((item: IUserRow) => {

        if (item.id == viewer.id.toString()) {

          found = true;
        }
      });
    }

    return found;
  }

  /** on page setup */
  ngOnInit() {

    Logger.debug('[ProjectDetailsComponent] init...');

    let pd: ProjectDetailsComponent = this;

    /* who is viewing the page */

    pd.viewer = pd.auth.userOfSession;

    pd.routeListen = pd.route.paramMap.subscribe((params: ParamMap) => {

      /* figure out the 'key' ... */

      pd.key = params.get('key');

      /* fetch the project... */

      pd.refresh();

    });

  }

  /** update the member list when members are removed */
  unlinkMember(removed: IUserRow) {

    let pd: ProjectDetailsComponent = this;

    pd.members = pd.members.filter(x => x.id != removed.id);
  }

  refreshMembers() {

    let pd: ProjectDetailsComponent = this;

    /* make sure date/time stamps are local the user's browser */

    /* sort the users by date then by name */

    if (pd.project.members) {

      pd.project.members = pd.project.members.map((item: IUserRow): IUserRow => {

        item.created_at = fromMySQLDate(item.created_at).toLocaleString().slice(0, 19).replace(',', '');
        item.updated_at = fromMySQLDate(item.updated_at).toLocaleString().slice(0, 19).replace(',', '');

        return item;
      });

      pd.project.members.sort((ua: IUserRow, ub: IUserRow): number => {

        let aParts: string[]  = ua.created_at.split(/[- :]/);
        let bParts: string[]  = ub.created_at.split(/[- :]/);

        let a: Date = new Date(Date.UTC(
          parseInt(aParts[0]),
          parseInt(aParts[1])-1,
          parseInt(aParts[2]),
          parseInt(aParts[3]),
          parseInt(aParts[4]),
          parseInt(aParts[5])
        ));

        let b: Date = new Date(Date.UTC(
          parseInt(bParts[0]),
          parseInt(bParts[1])-1,
          parseInt(bParts[2]),
          parseInt(bParts[3]),
          parseInt(bParts[4]),
          parseInt(bParts[5])
        ));

        /* sort first by date */

        let cmp: number = b.valueOf() - a.valueOf();

        if (cmp != 0) {

          return cmp;
        }

        /* dates are the same, sort by name */

        return ua.display_name.localeCompare(ub.display_name);
      });
    }

    if (pd.project.admins) {

      pd.project.admins = pd.project.admins.map((item: IUserRow): IUserRow => {

        item.created_at = fromMySQLDate(item.created_at).toLocaleString().slice(0, 19).replace(',', '');
        item.updated_at = fromMySQLDate(item.updated_at).toLocaleString().slice(0, 19).replace(',', '');

        return item;
      });

      pd.project.admins.sort((ua: IUserRow, ub: IUserRow): number => {

        let aParts: string[]  = ua.created_at.split(/[- :]/);
        let bParts: string[]  = ub.created_at.split(/[- :]/);

        let a: Date = new Date(Date.UTC(
          parseInt(aParts[0]),
          parseInt(aParts[1])-1,
          parseInt(aParts[2]),
          parseInt(aParts[3]),
          parseInt(aParts[4]),
          parseInt(aParts[5])
        ));

        let b: Date = new Date(Date.UTC(
          parseInt(bParts[0]),
          parseInt(bParts[1])-1,
          parseInt(bParts[2]),
          parseInt(bParts[3]),
          parseInt(bParts[4]),
          parseInt(bParts[5])
        ));

        /* sort first by date */

        let cmp: number = b.valueOf() - a.valueOf();

        if (cmp != 0) {

          return cmp;
        }

        /* dates are the same, sort by name */

        return ua.display_name.localeCompare(ub.display_name);
      });
    }

    if (pd.project.pending_users) {

      pd.project.pending_users = pd.project.pending_users.map((item: IUserRow): IUserRow => {

        item.created_at = fromMySQLDate(item.created_at).toLocaleString().slice(0, 19).replace(',', '');
        item.updated_at = fromMySQLDate(item.updated_at).toLocaleString().slice(0, 19).replace(',', '');

        return item;
      });

      pd.project.pending_users.sort((ua: IUserRow, ub: IUserRow): number => {

        let aParts: string[]  = ua.created_at.split(/[- :]/);
        let bParts: string[]  = ub.created_at.split(/[- :]/);

        let a: Date = new Date(Date.UTC(
          parseInt(aParts[0]),
          parseInt(aParts[1])-1,
          parseInt(aParts[2]),
          parseInt(aParts[3]),
          parseInt(aParts[4]),
          parseInt(aParts[5])
        ));

        let b: Date = new Date(Date.UTC(
          parseInt(bParts[0]),
          parseInt(bParts[1])-1,
          parseInt(bParts[2]),
          parseInt(bParts[3]),
          parseInt(bParts[4]),
          parseInt(bParts[5])
        ));

        /* sort first by date */

        let cmp: number = b.valueOf() - a.valueOf();

        if (cmp != 0) {

          return cmp;
        }

        /* dates are the same, sort by name */

        return ua.display_name.localeCompare(ub.display_name);
      });
    }




  }

  /** they picked a user from the auto-complete "add user" dropdown, add them! */
  addMember(event) {

    let pd: ProjectDetailsComponent = this;
    let toLink: IUserRow = pd.newUser;

    pd.newUser = null;

    Logger.debug(`[ProjectDetailsComponent] adding member:`, toLink);

    pd.users.linkMember(pd.project.id, Number(toLink.id))
      .pipe(first())
      .subscribe({

        next: (data: any) => {

          if (data.result) {

            data = data.result;
          }

          pd.project = data;

          pd.refreshMembers();

          pd.members = pd.project.members;
          pd.admins  = pd.project.admins;

          Logger.debug(`[SubdivisionListComponent] linked!`, pd.project);

          pd.cdRef.detectChanges();

        },

        error: (error: any): void => {

          Logger.error(`[ProjectDetailsComponent] could not link new member:`, error);
        }

      });

  }

  /** format the items in the autocomplete user dropdown */
  formatMatch(match: string) {

    let pd: ProjectDetailsComponent = this;

    return match.replace(pd.lastTerm, `<span class="autocomplete-hightlight-term">${pd.lastTerm}</span>`);
  }

  /** helper for new user auto-complete, to lookup partial user matches */
  search(event) {

    let term: string                = event.query;
    let pd: ProjectDetailsComponent = this;

    Logger.debug(`[ProjectDetailsComponent] need auto-complete on '${term}'...`);

    pd.lastTerm = term;

    pd.users.autocomplete(term)
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          if (data.result) {

            data = data.result;
          }

          pd.searchUsers = [];

          data.forEach((item) => {

            /* only keep the matches which are not already members of this project */

            if (pd.members.find(x => x.id == item.id)) {

              return;

            }

            if (item.is_pending) {

              /* we deal with pending users directly. */

              return;
            }

            pd.searchUsers.push(item);

            pd.cdRef.detectChanges();

          });
        },

        error: (error: any): void => {

          Logger.error(`[ProjectDetailsComponent] could not autocomplete: `, error);
        }

      });
  }

  /** set a new name for the given project */
  saveProjectName(id: number, name: string) {

    let pd: ProjectDetailsComponent = this;
    let prj: IProjectRow            = pd.project;

    if (!prj) {

      return ;
    }

    /* update locally */

    pd.project.name = name;

    /* update the database... */

    pd.prjservice.update(id, {name})
      .pipe(first())
      .subscribe({

        next: (data: any) => {

          Logger.debug(`[ProjectDetailsComponent] saved name: `, data);

          pd.cdRef.detectChanges();
        },

        error: (error: any): void => {

          Logger.error(`[ProjectDetailsComponent] could not save new organization name: `, error);

        }

      });

  }

  prjEditNameKey(event, id: number, component: Inplace) {

    let pd: ProjectDetailsComponent = this;
    let value: string = $(`.prjclass-name-${id} input`).val().toString();

    if (event.key != 'Enter') {

      return ;
    }

    Logger.debug(`[ProjectDetailsComponent] project editor RETURN (${id}) => '${value}': `, event);

    pd.saveProjectName(id, value);

    component.deactivate();
  }

  prjEditNameDeactivate(id: number, component: Inplace) {

    let pd: ProjectDetailsComponent = this;
    let value: string = $(`.prjclass-name-${id} input`).val().toString();

    Logger.debug(`[ProjectDetailsComponent] deactivating project name editor (${id}) => '${value}': `, component);

    pd.saveProjectName(id, value);

  }

  prjEditNameActivate(id: number, component: Inplace) {

    let pd: ProjectDetailsComponent = this;

    Logger.debug(`[ProjectDetailsComponent] activating project name editor (${id}): `, component);




  }

  /** set a new description for the given organization */
  saveProjectLogo(id: number, logo_url: string) {

    let pd: ProjectDetailsComponent = this;
    let prj: IProjectRow            = pd.project;

    if (!prj) {

      return ;
    }

    /* update locally */

    pd.project.logo_url = logo_url;

    /* update the database... */

    pd.prjservice.update(id, {logo_url})
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          Logger.debug(`[ProjectDetailsComponent] saved logo: `, data);

          pd.cdRef.detectChanges();
        },

        error: (error: any): void => {

          Logger.error(`[ProjectDetailsComponent] could not save new project logo: `, error);

        }

      });

  }

  prjEditLogoKey(event, id: number, component: Inplace) {

    let pd: ProjectDetailsComponent = this;
    let value: string = $(`.prjclass-logo-${id} input`).val().toString();

    if (event.key != 'Enter') {

      return ;
    }

    Logger.debug(`[ProjectDetailsComponent] project logo editor RETURN (${id}) => '${value}': `, event);

    pd.saveProjectLogo(id, value);

    component.deactivate();
  }

  prjEditLogoDeactivate(id: number, component: Inplace) {

    let pd: ProjectDetailsComponent = this;
    let value: string = $(`.prjclass-logo-${id} input`).val().toString();

    Logger.debug(`[ProjectDetailsComponent] deactivating project logo editor (${id}) => '${value}': `, component);

    pd.saveProjectLogo(id, value);

  }

  prjEditLogoActivate(id: number, component: Inplace) {

    let pd: ProjectDetailsComponent = this;

    Logger.debug(`[ProjectDetailsComponent] activating project logo editor (${id}): `, component);



  }

  /** set a new description for the given organization */
  saveProjectDescription(id: number, description: string) {

    let pd: ProjectDetailsComponent = this;
    let prj: IOrganizationRow            = pd.project;

    if (!prj) {

      return ;
    }

    /* update locally */

    pd.project.description = description;

    /* update the database... */

    pd.prjservice.update(id, {description})
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          Logger.debug(`[ProjectDetailsComponent] saved description: `, data);

          pd.cdRef.detectChanges();
        },

        error: (error: any): void => {

          Logger.error(`[ProjectDetailsComponent] could not save new project description: `, error);

        }

      });

  }

  prjEditDescKey(event, id: number, component: Inplace) {

    let pd: ProjectDetailsComponent = this;
    let value: string = $(`.prjclass-desc-${id} input`).val().toString();

    if (event.key != 'Enter') {

      return ;
    }

    Logger.debug(`[OrganizationDetailsComponent] project desc editor RETURN (${id}) => '${value}': `, event);

    pd.saveProjectDescription(id, value);

    component.deactivate();
  }

  prjEditDescDeactivate(id: number, component: Inplace) {

    let pd: ProjectDetailsComponent = this;
    let value: string = $(`.prjclass-desc-${id} input`).val().toString();

    Logger.debug(`[ProjectDetailsComponent] deactivating project desc editor (${id}) => '${value}': `, component);

    pd.saveProjectDescription(id, value);

  }

  prjEditDescActivate(id: number, component: Inplace) {

    let pd: ProjectDetailsComponent = this;

    Logger.debug(`[ProjectDetailsComponent] activating project desc editor (${id}): `, component);



  }

  /** render the page */
  refresh(): void {

    let pd: ProjectDetailsComponent = this;

    Logger.debug(`[ProjectDetailsComponent] data fill for ${pd.key}`);

    pd.prjservice.dump(pd.key)
      .pipe(first())
      .subscribe({

        next: (data: any) => {

          pd.project = null;

          if (data.result) {

            data = data.result;
          }

          pd.project = data.project;

          pd.refreshMembers();

          pd.members = data.project.members;
          pd.admins  = data.project.admins;

          Logger.debug(`[ProjectDetailsComponent] project: `, pd.project);

          pd.allSubs = [];

          data.project.org_subs.forEach((item) => {

            pd.allSubs.push(item);
          });

          pd.allSubs.sort((a: IProjectRow, b: IProjectRow) => {

            return a.name.localeCompare(b.name);
          });

          pd.cdRef.detectChanges();
        },

        error: (error: any): void => {

          Logger.error(`[ProjectDetailsComponent] could not fetch project details: `, error);

        }

      });
  }

  /** just approve a pending user */
  approve(user: IUserRow): void {

    let pd: ProjectDetailsComponent = this;

    Logger.debug(`[ProjectDetailsComponent] approve only on:`, user);

    pd.users.update(user.id, {
      is_pending: false,
      is_active: true,
      is_suspended: false
    })
      .pipe(first())
      .subscribe({

        next: (data: any) => {

          if (data.result) {

            data = data.result;
          }

          Logger.debug(`[ProjectDetailsComponent] edited!`, data);

          pd.project.pending_users = pd.project.pending_users.filter(x => x.id != data.id);

          pd.cdRef.detectChanges();
        },

        error: (error: any): void => {

          Logger.error(`[ProjectDetailsComponent] could not approve new user:`, error);
        }

      });

  }

  /** approve a pending user, AND add them as an organizational admin as well */
  approveAndLink(user: IUserRow) {

    let pd: ProjectDetailsComponent = this;

    Logger.debug(`[ProjectDetailsComponent] approve and add as member on:`, user);

    /* just link them in as normal; the backend will do an implied approval when it links */

    pd.users.linkMember(pd.project.id, Number(user.id))
      .pipe(first())
      .subscribe({

        next: (data: any) => {

          if (data.result) {

            data = data.result;
          }

          pd.project = data;

          pd.refreshMembers();

          pd.members = data.project.members;
          pd.admins  = data.project.admins;

          Logger.debug(`[ProjectDetailsComponent] linked!`, pd.project);

          pd.cdRef.detectChanges();
        },

        error: (error: any): void => {

          Logger.error(`[ProjectDetailsComponent] could not approve/link new member:`, error);
        }

      });

  }

  /** cleanup! */
  ngOnDestroy() {

    let pd: ProjectDetailsComponent = this;

    pd.routeListen.unsubscribe();
  }

}

